// Active-linked-algo set shared by the Desk panels and the Analytics page.
//
// ALL scope (selectedId === null) → union of active (active=1) links across
// every non-retired account — the accounts viewable in the AccountPicker.
// Single-account scope → that account's active links only.
//
// Returns null when the links aren't loaded or the fetch failed, so callers
// can fail open (show unfiltered) instead of blanking on a transient error.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { accounts as accountsApi } from "../api/endpoints";
import type { Account } from "../api/types";

export function useActiveLinkedAlgoIds(
  accounts: Account[],
  selectedId: number | null,
) {
  const viewableAccounts = useMemo(() => accounts.filter((a) => !a.retired), [accounts]);
  const linksQ = useQuery({
    queryKey: ["accounts", "matrix-links", viewableAccounts.map((a) => a.id)],
    queryFn: async () => {
      const results = await Promise.all(viewableAccounts.map((a) => accountsApi.links(a.id)));
      return results.flat();
    },
    enabled: viewableAccounts.length > 0,
    refetchInterval: 5000,
  });

  const linkedAlgoIds = useMemo<Set<number> | null>(() => {
    if (linksQ.isError || !linksQ.data) return null;
    const relevant = selectedId == null
      ? linksQ.data
      : linksQ.data.filter((l) => l.account_id === selectedId);
    return new Set(relevant.filter((l) => l.active).map((l) => l.algo_id));
  }, [linksQ.data, linksQ.isError, selectedId]);

  return { linkedAlgoIds, linksQ };
}
